/**
 * PdfCanvasPreview.jsx
 * Zeigt ein PDF seitenweise als Bild an (pdf.js) – unabhängig davon, ob der Browser PDFs
 * selbst anzeigt (Chrome-Einstellung „PDFs herunterladen“ verhindert die eingebettete Vorschau).
 * pdf.js wird beim ersten Aufruf von cdnjs geladen; das PDF selbst verlässt den Browser nicht.
 * Nach dem Umzug (H3) als Paket mitliefern statt vom CDN.
 */
import { useEffect, useRef, useState } from 'react';

const PDFJS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

let loader = null;

function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (!loader) {
    loader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PDFJS_URL;
      script.async = true;
      script.onload = () => {
        if (window.pdfjsLib) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_URL;
          resolve(window.pdfjsLib);
        } else {
          loader = null;
          reject(new Error('pdf.js nicht verfügbar'));
        }
      };
      script.onerror = () => {
        loader = null;
        reject(new Error('pdf.js konnte nicht geladen werden'));
      };
      document.head.appendChild(script);
    });
  }
  return loader;
}

export default function PdfCanvasPreview({ blob, fallback = null }) {
  const pagesRef = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const el = pagesRef.current;
    if (!blob || !el) return undefined;
    setStatus('loading');
    while (el.firstChild) el.removeChild(el.firstChild);

    (async () => {
      try {
        const lib = await loadPdfJs();
        const data = new Uint8Array(await blob.arrayBuffer());
        const pdf = await lib.getDocument({ data }).promise;
        const available = Math.max((el.clientWidth || 800) - 32, 320);
        const ratio = window.devicePixelRatio || 1;
        for (let n = 1; n <= pdf.numPages; n += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const scale = Math.min(2, available / base.width);
          const viewport = page.getViewport({ scale: scale * ratio });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
          canvas.style.height = `${Math.floor(viewport.height / ratio)}px`;
          canvas.style.display = 'block';
          canvas.style.margin = '0 auto 16px auto';
          canvas.style.background = '#ffffff';
          canvas.style.boxShadow = '0 1px 4px rgba(0,0,0,0.15)';
          canvas.setAttribute('aria-label', `Seite ${n} von ${pdf.numPages}`);
          el.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          if (n === 1 && !cancelled) setStatus('ok');
        }
      } catch (error) {
        console.error('PDF-Vorschau:', error);
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blob]);

  return (
    <div className="relative w-full" style={{ height: '75vh' }}>
      <div ref={pagesRef} className="w-full h-full overflow-y-auto rounded-lg border border-border bg-muted/40 p-4" />
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-card rounded-lg border border-border">
          {fallback}
        </div>
      )}
    </div>
  );
}
